<template>
    <div class="row">
        <div class="col-12 col-md-5 offset-md-1">
            <div class="form-group">
                <label for="member-id"> Member ID </label>
                <input
                    class="form-control"
                    maxlength="50"
                    name="member-id"
                    type="text"
                    placeholder="Enter Member ID"
                    v-model="payer.MemberId"
                />
            </div>
            <div class="form-group">
                <label for="group-name"> Group Name </label>
                <input
                    class="form-control"
                    maxlength="50"
                    name="group-name"
                    type="text"
                    placeholder="Enter Group Name"
                    v-model="payer.GroupName"
                />
            </div>
            <div class="form-group">
                <label for="group-id"> Group ID </label>
                <input
                    class="form-control"
                    maxlength="50"
                    name="group-id"
                    type="text"
                    placeholder="Enter Group ID"
                    v-model="payer.GroupId"
                />
            </div>
            <div class="form-group">
                <label for="relationship-to-insured">
                    Relationship to Insured
                    <span class="text-danger">*</span>
                </label>
                <select
                    class="form-select"
                    name="relationship-to-insured"
                    v-model="payer.RelationshipToInsured"
                    @blur="v$.payer.RelationshipToInsured.$touch"
                >
                    <option :value="null">Select Relationship to Insured</option>
                    <option
                        v-for="[key, value] of enumToMap(relationshipToInsuredEnum)"
                        :key="key"
                        :value="value.value"
                    >
                        {{ value.name }}
                    </option>
                </select>
                <span v-if="v$.payer.RelationshipToInsured.$error" class="field-invalid"
                    >Relationship is required.</span
                >
            </div>
            <div class="form-group">
                <label for="group-id"> Case Manager </label>
                <input
                    class="form-control"
                    maxlength="50"
                    name="group-id"
                    type="text"
                    placeholder="Enter Case Manager"
                    v-model="payer.CaseManager"
                />
            </div>
            <div class="form-group">
                <label for="employer-name"> Employer Name </label>
                <input
                    class="form-control"
                    maxlength="50"
                    name="employer-name"
                    type="text"
                    placeholder="Enter Employer Name"
                    v-model="payer.EmployerName"
                />
            </div>
        </div>
        <div class="col-12 col-md-5">
            <div class="form-group" v-if="payer.Type === roomAndBoardTypeValue">
                <label for="bill-as"> Bill As </label>
                <select class="form-select" name="bill-as" v-model="payer.BillAsType">
                    <option v-for="[key, value] of enumToMap(payerSequenceCodeEnum)" :key="key" :value="value.value">
                        {{ value.name }}
                    </option>
                </select>
            </div>
            <div class="form-group">
                <label for="deductible-amount"> Deductible Amount </label>
                <input
                    class="form-control"
                    maxlength="50"
                    name="deductible-amount"
                    type="text"
                    placeholder="Enter Deductible Amount"
                    v-model="payer.DeductibleAmount"
                    v-number-only
                />
            </div>
            <div class="form-group d-flex flex-row justify-content-between">
                <div>
                    <label for="has-deductible-met" class="me-2"> Deductible Met </label>
                    <div class="btn-group btn-group-toggle" data-bs-toggle="buttons">
                        <label
                            :class="`btn btn-outline-secondary ${payer.DeductibleMet ? 'active' : ''}`"
                            @click="hasDeductibleMetChanged(true)"
                        >
                            <input type="radio" hidden name="has-deductible-met" autocomplete="off" /> Yes
                        </label>
                        <label
                            :class="`btn btn-outline-secondary ${payer.DeductibleMet ? '' : 'active'}`"
                            @click="hasDeductibleMetChanged(false)"
                        >
                            <input type="radio" hidden name="has-deductible-met" autocomplete="off" /> No
                        </label>
                    </div>
                </div>
            </div>
            <div class="form-group">
                <label for="copay-amount"> Copay Amount </label>
                <input
                    class="form-control"
                    maxlength="50"
                    name="copay-amount"
                    type="text"
                    placeholder="Enter Copay Amount"
                    v-model="payer.CopayAmount"
                />
            </div>
            <template v-if="showSubscriberName">
                <div class="form-group">
                    <label for="subs-first-name"> Subscriber First Name <span class="text-danger">*</span> </label>
                    <input
                        class="form-control"
                        name="subs-first-name"
                        type="text"
                        placeholder="Enter Subscriber First Name"
                        v-model="payer.SubscriberFirstName"
                        @blur="v$.payer.SubscriberFirstName.$touch"
                    />
                    <span v-if="v$.payer.SubscriberFirstName.$error" class="field-invalid"
                        >Subscriber First Name is required.</span
                    >
                </div>
                <div class="form-group">
                    <label for="subs-last-name"> Subscriber Last Name <span class="text-danger">*</span> </label>
                    <input
                        class="form-control"
                        name="subs-last-name"
                        type="text"
                        placeholder="Enter Subscriber Last Name"
                        v-model="payer.SubscriberLastName"
                        @blur="v$.payer.SubscriberLastName.$touch"
                    />
                    <span v-if="v$.payer.SubscriberLastName.$error" class="field-invalid"
                        >Subscriber Last Name is required.</span
                    >
                </div>
            </template>
            <div class="form-group">
                <label for="copay-amount"> Comments </label>
                <textarea
                    class="form-control"
                    id="comments"
                    v-model="payer.Comments"
                    rows="4"
                    maxlength="300"
                ></textarea>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";

import { enumToMap } from "../../../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { HospicePatientPayer } from "../../../../../../../resources-vue/vue-models/hospice-patient-payer";

export default defineComponent({
    props: {
        payer: {
            type: HospicePatientPayer
        },
        relationshipToInsuredEnum: {
            default: null
        },
        payerSequenceCodeEnum: {
            default: null
        },
        hasDeductibleMetChanged: {
            type: Function
        },
        isApplicationPalliative: {
            type: Boolean
        },
        showSubscriberName: {
            type: Boolean
        },
        roomAndBoardTypeValue: {
            type: Number
        },
        palliativeCareTypeValue: {
            type: Number
        },
        requireAuthorizationChanged: {
            type: Function
        }
    },
    data() {
        return {
            v$: useVuelidate(),
            enumToMap
        };
    },

    validations() {
        return {
            payer: {
                RelationshipToInsured: { required },
                SubscriberFirstName: {
                    required: requiredIf(() => {
                        return this.showSubscriberName;
                    })
                },
                SubscriberLastName: {
                    required: requiredIf(() => {
                        return this.showSubscriberName;
                    })
                }
            }
        };
    }
});
</script>
